"use client";

import { useState, type FC } from "react";
import { ChevronDownIcon, ChevronRightIcon, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface JsonDisplayProps {
  data: unknown;
  className?: string;
}

/**
 * Renders JSON data in a human-readable format.
 * - Objects become key-value cards
 * - Arrays become bullet lists
 * - Primitives render inline
 * - Special handling for common patterns (status, score, etc.)
 */
export const JsonDisplay: FC<JsonDisplayProps> = ({ data, className }) => {
  if (data === null || data === undefined) {
    return <span className="text-muted-foreground italic">null</span>;
  }

  if (typeof data === "string") {
    return <span className="text-foreground">{data}</span>;
  }

  if (typeof data === "number" || typeof data === "boolean") {
    return <span className="text-amber-400">{String(data)}</span>;
  }

  if (Array.isArray(data)) {
    return <ArrayDisplay data={data} />;
  }

  if (typeof data === "object") {
    return <ObjectDisplay data={data as Record<string, unknown>} className={className} />;
  }

  return <span>{String(data)}</span>;
};

/**
 * Renders an array as a list
 */
const ArrayDisplay: FC<{ data: unknown[] }> = ({ data }) => {
  if (data.length === 0) {
    return <span className="text-muted-foreground italic">empty list</span>;
  }

  // If all items are strings, render as bullet list
  const allStrings = data.every(item => typeof item === "string");
  if (allStrings) {
    return (
      <ul className="list-disc list-inside space-y-1 text-sm">
        {data.map((item, idx) => (
          <li key={idx} className="text-foreground">{item as string}</li>
        ))}
      </ul>
    );
  }

  // Otherwise render each item
  return (
    <div className="space-y-2">
      {data.map((item, idx) => (
        <div key={idx} className="pl-2 border-l-2 border-muted">
          <JsonDisplay data={item} />
        </div>
      ))}
    </div>
  );
};

/**
 * Renders an object as key-value pairs with smart formatting
 */
const ObjectDisplay: FC<{ data: Record<string, unknown>; className?: string }> = ({ data, className }) => {
  const keys = Object.keys(data);

  if (keys.length === 0) {
    return <span className="text-muted-foreground italic">empty</span>;
  }

  // Check for common patterns
  const status = data.status as string | undefined;
  const hasAssessment = "assessment" in data && typeof data.assessment === "object";

  return (
    <div className={cn("space-y-2 text-sm", className)}>
      {/* Status badge */}
      {status && (
        <StatusBadge status={status} message={data.message as string | undefined} />
      )}

      {/* Render each field */}
      {keys.map(key => {
        // Skip status if we already rendered it as badge
        if (key === "status") return null;
        // Skip message if it was shown with status
        if (key === "message" && status) return null;

        const value = data[key];

        // Special rendering for assessment
        if (key === "assessment" && hasAssessment) {
          return <AssessmentDisplay key={key} assessment={value as Record<string, unknown>} />;
        }

        // Special rendering for score
        if (key === "score" && typeof value === "number") {
          return <ScoreDisplay key={key} score={value} />;
        }

        // Special rendering for evidence/items arrays
        if ((key === "evidence" || key === "items" || key === "reasons") && Array.isArray(value)) {
          return (
            <FieldGroup key={key} label={key}>
              <ArrayDisplay data={value} />
            </FieldGroup>
          );
        }

        // Nested object
        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          return (
            <CollapsibleField key={key} label={key}>
              <ObjectDisplay data={value as Record<string, unknown>} />
            </CollapsibleField>
          );
        }

        // Simple field
        return (
          <FieldRow key={key} label={key}>
            <JsonDisplay data={value} />
          </FieldRow>
        );
      })}
    </div>
  );
};

/**
 * Status badge with icon
 */
const StatusBadge: FC<{ status: string; message?: string }> = ({ status, message }) => {
  const isSuccess = status === "success" || status === "ok" || status === "completed";
  const isError = status === "error" || status === "failed" || status === "failure";

  return (
    <div className={cn(
      "flex items-start gap-2 p-2 rounded-md",
      isSuccess && "bg-emerald-500/10 text-emerald-400",
      isError && "bg-red-500/10 text-red-400",
      !isSuccess && !isError && "bg-blue-500/10 text-blue-400"
    )}>
      {isSuccess && <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />}
      {isError && <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
      {!isSuccess && !isError && <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
      <div>
        <span className="font-medium capitalize">{status}</span>
        {message && <p className="text-foreground mt-1">{message}</p>}
      </div>
    </div>
  );
};

/**
 * Score display with visual indicator
 */
const ScoreDisplay: FC<{ score: number; maxScore?: number }> = ({ score, maxScore = 5 }) => {
  const percentage = (score / maxScore) * 100;

  return (
    <div className="flex items-center gap-3">
      <span className="text-muted-foreground text-xs uppercase tracking-wide">Score</span>
      <div className="flex items-center gap-2">
        <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full transition-all",
              percentage >= 80 ? "bg-emerald-500" :
              percentage >= 60 ? "bg-yellow-500" :
              percentage >= 40 ? "bg-orange-500" : "bg-red-500"
            )}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="font-semibold text-foreground">{score}/{maxScore}</span>
      </div>
    </div>
  );
};

/**
 * Assessment display (for evaluation results)
 */
const AssessmentDisplay: FC<{ assessment: Record<string, unknown> }> = ({ assessment }) => {
  const score = assessment.score as number | undefined;
  const explanation = assessment.explanation as string | undefined;
  const evidence = assessment.evidence as string[] | undefined;

  return (
    <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
      {score !== undefined && <ScoreDisplay score={score} />}
      {explanation && (
        <p className="text-foreground leading-relaxed">{explanation}</p>
      )}
      {evidence && evidence.length > 0 && (
        <FieldGroup label="Evidence">
          <ArrayDisplay data={evidence} />
        </FieldGroup>
      )}
    </div>
  );
};

/**
 * Simple field row
 */
const FieldRow: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start gap-2">
    <span className="text-muted-foreground text-xs uppercase tracking-wide min-w-[80px] pt-0.5">{formatLabel(label)}</span>
    <div className="text-foreground flex-1">{children}</div>
  </div>
);

/**
 * Field group with label above
 */
const FieldGroup: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="space-y-1">
    <span className="text-muted-foreground text-xs uppercase tracking-wide">{formatLabel(label)}</span>
    <div className="pl-2">{children}</div>
  </div>
);

/**
 * Collapsible field for nested objects
 */
const CollapsibleField: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
      >
        {expanded ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
        <span className="text-xs uppercase tracking-wide">{formatLabel(label)}</span>
      </button>
      {expanded && <div className="pl-4 mt-1">{children}</div>}
    </div>
  );
};

/**
 * Format a key as a readable label
 */
function formatLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .trim();
}

export default JsonDisplay;
